/**
 * blacklistUtils — Shared helpers for checking emails/domains against the
 * user's blacklist (Firestore collection `World/${userId}/blacklist`).
 *
 * The same blacklist that is enforced during CSV import (WorldDbUploader)
 * also has to be enforced AFTER the import, because Web Research and the
 * DM Email Finder discover new email addresses on contacts that didn't
 * have an email at import time. Without this guard, blacklisted addresses
 * would silently re-enter the system through enrichment.
 *
 * Blacklist entries are stored with the email/domain as the document ID:
 *   - Email entries:  "peter@firma.de"  (type: 'email')
 *   - Domain entries: "@firma.de"       (type: 'domain') — blocks every
 *     address ending in that domain
 */
import { collection, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../firebaseConfig';

// Must mirror the server-side cap in functions/checkBlacklistBatch.js. We
// chunk client-side so callers don't have to think about it.
const BATCH_LOOKUP_CAP = 20000;
const BATCH_LOOKUP_CHUNK = 5000;

/**
 * Server-side blacklist lookup that scales with the input set, not with the
 * blacklist size. This is THE way to check the blacklist from the client —
 * inbox, World DB table and bulk research all go through it. `loadBlacklistSets`
 * below is deprecated in its favour.
 *
 * Cost model: ~uniqueEmails + uniqueDomains Firestore reads per call,
 * regardless of how big the blacklist gets. Compare to `loadBlacklistSets`
 * which always reads the full collection.
 *
 * Returns the same `{ emails: Set, domains: Set }` shape that
 * `loadBlacklistSets` returns, so callers can reuse `isEmailBlacklisted` /
 * `filterBlacklistedEmails` / `stripBlacklistedEmailsFromDms` unchanged.
 *
 * @param {string[]} emails - addresses to test (will be normalized + deduped)
 * @param {string} [userId] - optional team-owner id for team-member callers
 * @returns {Promise<{ emails: Set<string>, domains: Set<string> }>}
 */
export const lookupBlacklistForEmails = async (emails, userId) => {
  const empty = { emails: new Set(), domains: new Set() };
  if (!Array.isArray(emails) || emails.length === 0) return empty;

  const callable = httpsCallable(functions, 'checkBlacklistBatch');
  const blockedEmails = new Set();
  const blockedDomains = new Set();

  // Chunk into batches the server will accept. We don't dedup here — the
  // server does that anyway and it keeps this helper cheap on cold paths.
  for (let i = 0; i < emails.length; i += BATCH_LOOKUP_CHUNK) {
    if (i >= BATCH_LOOKUP_CAP) {
      console.warn(
        `[blacklistUtils] truncating lookup at ${BATCH_LOOKUP_CAP} emails — ` +
        `received ${emails.length}. Split your job into smaller calls.`
      );
      break;
    }
    const slice = emails.slice(i, Math.min(i + BATCH_LOOKUP_CHUNK, BATCH_LOOKUP_CAP));
    try {
      const res = await callable({ emails: slice, userId });
      const data = res?.data || {};
      (data.blockedEmails || []).forEach(e => blockedEmails.add(e));
      (data.blockedDomains || []).forEach(d => blockedDomains.add(d));
    } catch (err) {
      console.error('[blacklistUtils] checkBlacklistBatch error:', err);
      // Don't silently let blocked addresses through — surface to caller.
      throw err;
    }
  }

  return { emails: blockedEmails, domains: blockedDomains };
};

/**
 * @deprecated Use `lookupBlacklistForEmails` instead. It returns the exact same
 * `{ emails, domains }` shape and works with the same `isEmailBlacklisted` /
 * `filterBlacklistedEmails` helpers, so it is a drop-in replacement.
 *
 * DO NOT ADD NEW CALLERS. This reads the ENTIRE blacklist collection, so its
 * cost scales with the blacklist rather than with what you are checking. On a
 * world with a bulk-imported blacklist (~28.000 entries) that was measured at
 * 18 MB over the wire — it made an inbox take 47 s to open on a normal
 * connection and was re-paid on every single load, because the Firestore
 * client here has no persistence. Every caller has since been migrated to
 * `lookupBlacklistForEmails` (inbox, World DB table, bulk research), which asks
 * the `checkBlacklistBatch` callable only about the addresses at hand.
 *
 * Kept only because a full snapshot is conceivably useful for a one-off
 * export/audit. If you reach for it in a render or load path, you want
 * `lookupBlacklistForEmails`.
 *
 * Loads the full blacklist into two Sets for O(1) lookups.
 * Returns { emails: Set<string>, domains: Set<string> } — both lowercase.
 */
export const loadBlacklistSets = async (userId) => {
  const empty = { emails: new Set(), domains: new Set() };
  if (!userId) return empty;
  try {
    const colRef = collection(firestore, `World/${userId}/blacklist`);
    const snap = await getDocs(colRef);
    const emails = new Set();
    const domains = new Set();
    snap.forEach(d => {
      const data = d.data() || {};
      const entry = (data.email || d.id || '').toLowerCase().trim();
      if (!entry) return;
      if (entry.startsWith('@')) domains.add(entry);
      else emails.add(entry);
    });
    return { emails, domains };
  } catch (err) {
    console.error('[blacklistUtils] loadBlacklistSets error:', err);
    return empty;
  }
};

/**
 * Returns true if the given email is blocked — either by an exact email
 * entry or by the domain it belongs to.
 */
export const isEmailBlacklisted = (email, sets) => {
  if (!email || typeof email !== 'string' || !sets) return false;
  const normalized = email.toLowerCase().trim();
  if (!normalized || !normalized.includes('@')) return false;
  if (sets.emails && sets.emails.has(normalized)) return true;
  const atIdx = normalized.indexOf('@');
  const domain = atIdx >= 0 ? normalized.substring(atIdx) : '';
  if (domain && sets.domains && sets.domains.has(domain)) return true;
  return false;
};

/**
 * Filters out any blacklisted emails from a list. Preserves order, keeps
 * only valid email strings.
 */
export const filterBlacklistedEmails = (emails, sets) => {
  if (!Array.isArray(emails) || emails.length === 0) return [];
  if (!sets || (sets.emails?.size === 0 && sets.domains?.size === 0)) return emails;
  return emails.filter(e => !isEmailBlacklisted(e, sets));
};

/**
 * Returns a copy of the decision-makers list with the `email` field cleared
 * on entries whose email collides with the blacklist. Other DM data (name,
 * role, socials, …) is preserved so the contact still shows the person.
 *
 * Returns { dms, removedCount } so callers can log/show user feedback.
 */
export const stripBlacklistedEmailsFromDms = (dms, sets) => {
  if (!Array.isArray(dms) || dms.length === 0) return { dms: dms || [], removedCount: 0 };
  if (!sets || (sets.emails?.size === 0 && sets.domains?.size === 0)) {
    return { dms, removedCount: 0 };
  }
  let removedCount = 0;
  const next = dms.map(dm => {
    if (dm && dm.email && isEmailBlacklisted(dm.email, sets)) {
      removedCount++;
      // Wipe email + verification flags so downstream UI treats it as
      // "no email found" instead of a usable address. Keep the DM record
      // itself so the contact still has the person + role + socials.
      const { email, emailVerified, emailSource, emailNote, ...rest } = dm;
      return {
        ...rest,
        emailSearched: true,
        emailNotFound: true,
        emailNote: 'Blocked by blacklist',
      };
    }
    return dm;
  });
  return { dms: next, removedCount };
};
